import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  Drawer,
  InputNumber,
  Popconfirm,
} from "antd";
import {
  FontSizeOutlined,
  MenuOutlined,
  ReloadOutlined,
  SearchOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import Parse from "parse";
import { AppLayout } from "./AppLayout";
import { TENANT_ID } from "../const";

const { Title, Text } = Typography;
const { TextArea } = Input;

// Parse-Klassen
const TextContent = Parse.Object.extend("APP_TextContent");
const MenuItem = Parse.Object.extend("APP_MenuItem");
type TextContent = Parse.Object;
type MenuItem = Parse.Object;

export interface MenuItemData {
  id?: string;
  key: string;
  title: string;
  route: string;
  icon: string;
  order: number;
  visible: boolean;
  parentId?: string;
  children?: MenuItemData[];
}

const AdminContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderCard = styled(Card)`
  margin-bottom: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;

  .ant-card-body {
    padding: 24px 32px;
  }

  h2.ant-typography {
    color: white !important;
    margin-bottom: 4px;
  }

  .ant-typography-secondary {
    color: rgba(255, 255, 255, 0.85) !important;
  }
`;

export const AdminRoute: React.FC = () => {
  // Text-Verwaltung State
  const [texts, setTexts] = useState<TextContent[]>([]);
  const [loadingTexts, setLoadingTexts] = useState(false);
  const [editingText, setEditingText] = useState<TextContent | null>(null);
  const [textDrawerOpen, setTextDrawerOpen] = useState(false);
  const [textForm] = Form.useForm();
  const [textFilter, setTextFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");

  // Menü-Verwaltung State
  const [menuItems, setMenuItems] = useState<MenuItemData[]>([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState<MenuItemData | null>(
    null
  );
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [menuForm] = Form.useForm();

  useEffect(() => {
    loadTexts();
    loadMenuItems();
  }, []);

  // Texte laden
  const loadTexts = async () => {
    setLoadingTexts(true);
    try {
      const tenant =
        Parse.Object.extend("OD3_Tenant").createWithoutData(TENANT_ID);
      const query = new Parse.Query(TextContent);
      query.equalTo("tenant", tenant);
      query.ascending("key");
      query.limit(1000);

      const results = await query.find();
      setTexts(results);
    } catch (err) {
      console.error(err);
      message.error("Fehler beim Laden der Texte");
    } finally {
      setLoadingTexts(false);
    }
  };

  // Menü-Items aus Parse laden
  const loadMenuItems = async () => {
    setLoadingMenu(true);
    try {
      const tenant =
        Parse.Object.extend("OD3_Tenant").createWithoutData(TENANT_ID);
      const query = new Parse.Query(MenuItem);
      query.equalTo("tenant", tenant);
      query.ascending("order");
      query.limit(200);

      const results = await query.find();

      // Parse-Objekte in MenuItemData konvertieren
      const items: MenuItemData[] = results.map((item) => ({
        id: item.id,
        key: item.get("key"),
        title: item.get("title"),
        route: item.get("route") || "",
        icon: item.get("icon") || "fa:circle",
        order: item.get("order") || 0,
        visible: item.get("visible") !== false,
        parentId: item.get("parent")?.id,
      }));

      // Hierarchie aufbauen
      const rootItems: MenuItemData[] = [];
      const itemMap = new Map<string, MenuItemData>();

      // Erst alle Items in Map speichern
      for (const item of items) {
        itemMap.set(item.id!, { ...item, children: [] });
      }

      // Dann Hierarchie aufbauen
      for (const item of items) {
        const mappedItem = itemMap.get(item.id!)!;
        if (item.parentId && itemMap.has(item.parentId)) {
          const parent = itemMap.get(item.parentId)!;
          parent.children = parent.children || [];
          parent.children.push(mappedItem);
        } else {
          rootItems.push(mappedItem);
        }
      }

      // Sortieren
      const sortItems = (items: MenuItemData[]) => {
        items.sort((a, b) => a.order - b.order);
        for (const item of items) {
          if (item.children?.length) {
            sortItems(item.children);
          }
        }
      };
      sortItems(rootItems);

      setMenuItems(rootItems);
    } catch (err) {
      console.error(err);
      message.error("Fehler beim Laden der Menü-Items");
    } finally {
      setLoadingMenu(false);
    }
  };

  // Menü-Item erstellen/bearbeiten
  const handleSaveMenuItem = async (values: any) => {
    try {
      const tenant =
        Parse.Object.extend("OD3_Tenant").createWithoutData(TENANT_ID);

      let menuItem: Parse.Object;
      if (editingMenuItem?.id) {
        // Bestehenden Eintrag laden
        const query = new Parse.Query(MenuItem);
        menuItem = await query.get(editingMenuItem.id);
      } else {
        // Neuen Eintrag erstellen
        menuItem = new MenuItem();
        menuItem.set("tenant", tenant);
      }

      menuItem.set("key", values.key);
      menuItem.set("title", values.title);
      menuItem.set("route", values.route || "");
      menuItem.set("icon", values.icon || "fa:circle");
      menuItem.set("order", values.order || 0);
      menuItem.set("visible", values.visible !== false);

      // Parent setzen
      if (values.parentId) {
        const parent = MenuItem.createWithoutData(values.parentId);
        menuItem.set("parent", parent);
      } else {
        menuItem.unset("parent");
      }

      await menuItem.save();

      message.success(
        editingMenuItem?.id ? "Menüpunkt aktualisiert" : "Menüpunkt erstellt"
      );
      setMenuModalOpen(false);
      setEditingMenuItem(null);
      menuForm.resetFields();
      loadMenuItems();
    } catch (err) {
      console.error(err);
      message.error("Fehler beim Speichern");
    }
  };

  // Menü-Item löschen
  const handleDeleteMenuItem = async (id: string) => {
    try {
      const query = new Parse.Query(MenuItem);
      const menuItem = await query.get(id);
      await menuItem.destroy();

      message.success("Menüpunkt gelöscht");
      loadMenuItems();
    } catch (err) {
      console.error(err);
      message.error("Fehler beim Löschen");
    }
  };

  // Sichtbarkeit umschalten
  const handleToggleVisibility = async (id: string, visible: boolean) => {
    try {
      const query = new Parse.Query(MenuItem);
      const menuItem = await query.get(id);
      menuItem.set("visible", visible);
      await menuItem.save();

      message.success(`Menüpunkt ${visible ? "eingeblendet" : "ausgeblendet"}`);
      loadMenuItems();
    } catch (err) {
      console.error(err);
      message.error("Fehler beim Speichern");
    }
  };

  // Text bearbeiten
  const handleEditText = (text: TextContent) => {
    setEditingText(text);
    setTextDrawerOpen(true);
    textForm.setFieldsValue({
      key: text.get("key"),
      category: text.get("category"),
      description: text.get("description"),
      isRichText: text.get("isRichText"),
      value_de: text.get("value_de"),
      value_en: text.get("value_en"),
      value_ar: text.get("value_ar"),
      value_tr: text.get("value_tr"),
      value_ru: text.get("value_ru"),
      value_uk: text.get("value_uk"),
      value_ro: text.get("value_ro"),
      value_bg: text.get("value_bg"),
    });
  };

  // Text speichern
  const handleSaveText = async (values: any) => {
    if (!editingText) return;

    try {
      editingText.set("key", values.key);
      editingText.set("category", values.category);
      editingText.set("description", values.description);
      editingText.set("isRichText", values.isRichText);
      editingText.set("value_de", values.value_de);
      editingText.set("value_en", values.value_en);
      editingText.set("value_ar", values.value_ar);
      editingText.set("value_tr", values.value_tr);
      editingText.set("value_ru", values.value_ru);
      editingText.set("value_uk", values.value_uk);
      editingText.set("value_ro", values.value_ro);
      editingText.set("value_bg", values.value_bg);

      await editingText.save();

      setTexts((prev) =>
        prev.map((t) => (t.id === editingText.id ? editingText : t))
      );
      message.success("Text gespeichert");
      setTextDrawerOpen(false);
    } catch (err) {
      console.error(err);
      message.error("Fehler beim Speichern");
    }
  };

  // Kategorien extrahieren
  const categories = Array.from(
    new Set(texts.map((t) => t.get("category")).filter(Boolean))
  ) as string[];

  // Texte filtern
  const filteredTexts = texts.filter((t) => {
    const key = t.get("key") || "";
    const matchesSearch =
      !textFilter ||
      key.toLowerCase().includes(textFilter.toLowerCase()) ||
      t.get("value_de")?.toLowerCase().includes(textFilter.toLowerCase());
    const matchesCategory =
      !categoryFilter || t.get("category") === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Flatten menu items für die Tabelle
  const flattenMenuItems = (
    items: MenuItemData[],
    level = 0
  ): (MenuItemData & { level: number })[] => {
    const result: (MenuItemData & { level: number })[] = [];
    for (const item of items) {
      result.push({ ...item, level });
      if (item.children?.length) {
        result.push(...flattenMenuItems(item.children, level + 1));
      }
    }
    return result;
  };

  const flatMenuItems = flattenMenuItems(menuItems);

  // Alle Items für Parent-Auswahl (flach)
  const allMenuItemsFlat = flatMenuItems.filter((item) => item.level < 2); // Max 2 Ebenen für Parent

  // Menüpunkt bearbeiten öffnen
  const openEditMenuItem = (item: MenuItemData) => {
    setEditingMenuItem(item);
    menuForm.setFieldsValue({
      key: item.key,
      title: item.title,
      route: item.route,
      icon: item.icon,
      order: item.order,
      visible: item.visible,
      parentId: item.parentId,
    });
    setMenuModalOpen(true);
  };

  // Neuen Menüpunkt hinzufügen
  const openAddMenuItem = (parentId?: string) => {
    setEditingMenuItem(null);
    menuForm.resetFields();
    if (parentId) {
      menuForm.setFieldsValue({ parentId, visible: true, order: 0 });
    } else {
      menuForm.setFieldsValue({ visible: true, order: 0 });
    }
    setMenuModalOpen(true);
  };

  return (
    <AppLayout>
      <AdminContainer>
        <HeaderCard>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Title level={2} style={{ margin: 0 }}>
                📝 Content-Verwaltung
              </Title>
              <Text type="secondary">Bearbeite Texte und Menü-Struktur</Text>
            </div>
          </div>
        </HeaderCard>

        <Tabs
          defaultActiveKey="texts"
          size="large"
          items={[
            {
              key: "texts",
              label: (
                <span>
                  <FontSizeOutlined /> Texte
                </span>
              ),
              children: (
                <>
                  <Card style={{ marginBottom: 16 }}>
                    <Space wrap>
                      <Input
                        placeholder="Suchen..."
                        prefix={<SearchOutlined />}
                        value={textFilter}
                        onChange={(e) => setTextFilter(e.target.value)}
                        style={{ width: 250 }}
                        allowClear
                      />
                      <Select
                        placeholder="Kategorie"
                        value={categoryFilter || undefined}
                        onChange={setCategoryFilter}
                        allowClear
                        style={{ width: 180 }}
                      >
                        {categories.map((cat) => (
                          <Select.Option key={cat} value={cat}>
                            {cat}
                          </Select.Option>
                        ))}
                      </Select>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={loadTexts}
                        loading={loadingTexts}
                      >
                        Aktualisieren
                      </Button>
                      <Text type="secondary">
                        {filteredTexts.length} von {texts.length} Texten
                      </Text>
                    </Space>
                  </Card>

                  <Table
                    dataSource={filteredTexts}
                    rowKey="id"
                    loading={loadingTexts}
                    size="small"
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                    columns={[
                      {
                        title: "Key",
                        dataIndex: "key",
                        key: "key",
                        width: 280,
                        render: (_: any, record: TextContent) => (
                          <Text code style={{ fontSize: 11 }}>
                            {record.get("key")}
                          </Text>
                        ),
                        sorter: (a: TextContent, b: TextContent) =>
                          (a.get("key") || "").localeCompare(
                            b.get("key") || ""
                          ),
                      },
                      {
                        title: "Kategorie",
                        key: "category",
                        width: 120,
                        render: (_: any, record: TextContent) =>
                          record.get("category") ? (
                            <Tag color="blue">{record.get("category")}</Tag>
                          ) : (
                            <Tag>-</Tag>
                          ),
                      },
                      {
                        title: "Deutsch",
                        key: "value_de",
                        ellipsis: true,
                        render: (_: any, record: TextContent) => (
                          <Text ellipsis style={{ maxWidth: 300 }}>
                            {record.get("value_de") || (
                              <Text type="secondary">–</Text>
                            )}
                          </Text>
                        ),
                      },
                      {
                        title: "Sprachen",
                        key: "languages",
                        width: 140,
                        render: (_: any, record: TextContent) => {
                          const langs = [];
                          if (record.get("value_de")) langs.push("DE");
                          if (record.get("value_en")) langs.push("EN");
                          if (record.get("value_ar")) langs.push("AR");
                          if (record.get("value_tr")) langs.push("TR");
                          if (record.get("value_ru")) langs.push("RU");
                          if (record.get("value_uk")) langs.push("UK");
                          if (record.get("value_ro")) langs.push("RO");
                          if (record.get("value_bg")) langs.push("BG");
                          return (
                            <Space size={2}>
                              {langs.map((l) => (
                                <Tag
                                  key={l}
                                  color="green"
                                  style={{ fontSize: 10, padding: "0 4px" }}
                                >
                                  {l}
                                </Tag>
                              ))}
                            </Space>
                          );
                        },
                      },
                      {
                        title: "",
                        key: "actions",
                        width: 80,
                        render: (_: any, record: TextContent) => (
                          <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEditText(record)}
                          >
                            Edit
                          </Button>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              key: "menu",
              label: (
                <span>
                  <MenuOutlined /> Menü-Struktur
                </span>
              ),
              children: (
                <>
                  <Card style={{ marginBottom: 16 }}>
                    <Space>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openAddMenuItem()}
                      >
                        Neuer Hauptmenüpunkt
                      </Button>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={loadMenuItems}
                        loading={loadingMenu}
                      >
                        Aktualisieren
                      </Button>
                      <Text type="secondary">
                        {flatMenuItems.length} Menüpunkte
                      </Text>
                    </Space>
                  </Card>

                  <Table
                    dataSource={flatMenuItems}
                    rowKey="id"
                    loading={loadingMenu}
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: "Key",
                        key: "key",
                        width: 120,
                        render: (_: any, record) => (
                          <Text code style={{ fontSize: 12 }}>
                            {record.key}
                          </Text>
                        ),
                      },
                      {
                        title: "Titel",
                        key: "title",
                        render: (_: any, record) => (
                          <Text
                            strong
                            style={{
                              paddingLeft: record.level * 24,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {record.level > 0 && (
                              <span style={{ color: "#999" }}>↳</span>
                            )}
                            {record.title}
                          </Text>
                        ),
                      },
                      {
                        title: "Route",
                        key: "route",
                        width: 280,
                        render: (_: any, record) => (
                          <Text code style={{ fontSize: 10 }}>
                            {record.route || "-"}
                          </Text>
                        ),
                      },
                      {
                        title: "Order",
                        key: "order",
                        width: 70,
                        render: (_: any, record) => (
                          <Text type="secondary">{record.order}</Text>
                        ),
                      },
                      {
                        title: "Sichtbar",
                        key: "visible",
                        width: 120,
                        render: (_: any, record) => (
                          <Switch
                            checked={record.visible}
                            onChange={(checked) =>
                              handleToggleVisibility(record.id!, checked)
                            }
                            checkedChildren="Ja"
                            unCheckedChildren="Nein"
                            size="small"
                          />
                        ),
                      },
                      {
                        title: "Aktionen",
                        key: "actions",
                        width: 180,
                        render: (_: any, record) => (
                          <Space size="small">
                            <Button
                              type="link"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => openAddMenuItem(record.id)}
                            >
                              Kind
                            </Button>
                            <Button
                              type="link"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => openEditMenuItem(record)}
                            />
                            <Popconfirm
                              title="Menüpunkt löschen?"
                              description="Dies löscht auch alle Untermenüpunkte."
                              onConfirm={() => handleDeleteMenuItem(record.id!)}
                              okText="Löschen"
                              cancelText="Abbrechen"
                            >
                              <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                              />
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
          ]}
        />

        {/* Text-Bearbeitung Drawer */}
        <Drawer
          title={`Text bearbeiten: ${editingText?.get("key")}`}
          open={textDrawerOpen}
          onClose={() => setTextDrawerOpen(false)}
          width={700}
          extra={
            <Button type="primary" onClick={() => textForm.submit()}>
              Speichern
            </Button>
          }
        >
          <Form form={textForm} layout="vertical" onFinish={handleSaveText}>
            <Form.Item label="Key" name="key">
              <Input disabled />
            </Form.Item>

            <Space style={{ width: "100%" }} size="large">
              <Form.Item
                label="Kategorie"
                name="category"
                style={{ width: 200 }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Rich Text"
                name="isRichText"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Space>

            <Form.Item label="Beschreibung" name="description">
              <Input placeholder="Interne Beschreibung..." />
            </Form.Item>

            <Tabs
              size="small"
              items={[
                {
                  key: "de",
                  label: "🇩🇪 Deutsch",
                  children: (
                    <Form.Item name="value_de">
                      <TextArea rows={6} />
                    </Form.Item>
                  ),
                },
                {
                  key: "en",
                  label: "🇬🇧 English",
                  children: (
                    <Form.Item name="value_en">
                      <TextArea rows={6} />
                    </Form.Item>
                  ),
                },
                {
                  key: "ar",
                  label: "🇸🇦 العربية",
                  children: (
                    <Form.Item name="value_ar">
                      <TextArea rows={6} />
                    </Form.Item>
                  ),
                },
                {
                  key: "tr",
                  label: "🇹🇷 Türkçe",
                  children: (
                    <Form.Item name="value_tr">
                      <TextArea rows={6} />
                    </Form.Item>
                  ),
                },
                {
                  key: "ru",
                  label: "🇷🇺 Русский",
                  children: (
                    <Form.Item name="value_ru">
                      <TextArea rows={6} />
                    </Form.Item>
                  ),
                },
                {
                  key: "uk",
                  label: "🇺🇦 Українська",
                  children: (
                    <Form.Item name="value_uk">
                      <TextArea rows={6} />
                    </Form.Item>
                  ),
                },
                {
                  key: "ro",
                  label: "🇷🇴 Română",
                  children: (
                    <Form.Item name="value_ro">
                      <TextArea rows={6} />
                    </Form.Item>
                  ),
                },
                {
                  key: "bg",
                  label: "🇧🇬 Български",
                  children: (
                    <Form.Item name="value_bg">
                      <TextArea rows={6} />
                    </Form.Item>
                  ),
                },
              ]}
            />
          </Form>
        </Drawer>

        {/* Menü-Bearbeitung Modal */}
        <Modal
          title={
            editingMenuItem?.id ? "Menüpunkt bearbeiten" : "Neuer Menüpunkt"
          }
          open={menuModalOpen}
          onCancel={() => {
            setMenuModalOpen(false);
            setEditingMenuItem(null);
            menuForm.resetFields();
          }}
          onOk={() => menuForm.submit()}
          okText="Speichern"
          cancelText="Abbrechen"
          width={600}
        >
          <Form form={menuForm} layout="vertical" onFinish={handleSaveMenuItem}>
            <Form.Item
              label="Key (z.B. A, A.1, B.2.1)"
              name="key"
              rules={[{ required: true, message: "Key ist erforderlich" }]}
            >
              <Input placeholder="A.1" />
            </Form.Item>

            <Form.Item
              label="Titel"
              name="title"
              rules={[{ required: true, message: "Titel ist erforderlich" }]}
            >
              <Input placeholder="Wasserdargebot und Wasserverbrauch" />
            </Form.Item>

            <Form.Item label="Route (URL-Pfad)" name="route">
              <Input placeholder="/wasserklaerer/wasserangebot" />
            </Form.Item>

            <Space style={{ width: "100%" }}>
              <Form.Item label="Icon" name="icon" style={{ width: 150 }}>
                <Input placeholder="fa:tint" />
              </Form.Item>

              <Form.Item
                label="Reihenfolge"
                name="order"
                style={{ width: 120 }}
              >
                <InputNumber min={0} />
              </Form.Item>

              <Form.Item
                label="Sichtbar"
                name="visible"
                valuePropName="checked"
              >
                <Switch defaultChecked />
              </Form.Item>
            </Space>

            <Form.Item label="Übergeordneter Menüpunkt" name="parentId">
              <Select allowClear placeholder="(Hauptebene)">
                {allMenuItemsFlat
                  .filter((item) => item.id !== editingMenuItem?.id)
                  .map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {"  ".repeat(item.level)}
                      {item.key} - {item.title}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </AdminContainer>
    </AppLayout>
  );
};
